import type { ImporterConfig } from '@/engine/Serialization/IImporter';
import { getConfig } from './TTVRImporterConfig';
import { SceneItemKind, type JSONConfig, Transparency } from '@/engine/app/interfaces';
import { config as configTTVR } from './planner-config_TTVR.ts';
import type { PlanningEntityRegistry } from '@/engine/DynamicPlanning/PlanningEntityRegistry.ts';
import { TTVRPlanningEntityKind } from './TTVRPlanningEntityKind.ts';
import { buildAlgorithm } from './TTVRAlgorithmBuilder';
import type { DataFlowBuilder } from '@/engine/DataFlow/DataFlowBuilder.ts';
import {
  buildLeafletCommonAlgorithm,
  buildPapillaryAlgorithm,
  buildPapillaryCommonAlgorithm
} from './TTVRVariableDistanceAlgorithmBuilder.ts';
import { ImagesMode, SliceType } from '@/engine/app/modules/images/interfaces.ts';
import { color, angleMeasurementRelatedStub, centralLineStub } from './configs/measurements.config.ts';
import { aquaColor, deepSkyBlueColorDarken, fuchsiaColor, neonGreenColor, redColor } from './configs/objects.config.ts';
import type { PlanningEntity } from '@/engine/DynamicPlanning/PlanningEntity.ts';
import { buildCommissureAlgorithm, buildCommissureCommonAlgorithm } from './TTVRCommissureAlgorithmBuilder.ts';
import { LightenDarkenColor } from '@/engine/utils/colorUtils';

export class TTVRConfigsFactory {
  private readonly _leaflet: Array<PlanningEntity>;

  private readonly _papillary: Array<PlanningEntity>;

  private readonly _commissure: Array<PlanningEntity>;

  public constructor(planningEntityRegistry: PlanningEntityRegistry) {
    this._leaflet = Array.from(planningEntityRegistry.getAll().values()).filter(
      (v) => v.kind == TTVRPlanningEntityKind.Leaflet
    );

    this._papillary = Array.from(planningEntityRegistry.getAll().values()).filter(
      (v) => v.kind == TTVRPlanningEntityKind.Papillary
    );
    this._commissure = Array.from(planningEntityRegistry.getAll().values()).filter(
      (v) => v.kind == TTVRPlanningEntityKind.Commissure
    );
  }

  public getImporterConfig(): ImporterConfig {
    const config = getConfig();

    config.algorithmBuilder = (builder: DataFlowBuilder): void => {
      buildAlgorithm(builder);
      for (const p of this._papillary) {
        buildPapillaryAlgorithm(builder, p.descriptor);
      }

      for (const c of this._commissure) {
        buildCommissureAlgorithm(builder, c.descriptor);
      }
    };

    if (!config.algorithmExtensions) {
      config.algorithmExtensions = new Map();
    }

    // Papillary muscles
    config.algorithmExtensions.set('papillaryCommonAlgorithm', (builder: DataFlowBuilder): void => {
      buildPapillaryCommonAlgorithm(
        builder,
        this._papillary.map((p) => p.descriptor)
      );
    });

    for (const p of this._papillary) {
      config.points.push({
        importName: p.importName.toLowerCase(),
        algoName: 'pt_' + p.descriptor + '_imported',
        isOptionalEntity: false,
        imagePhaseKind: 'Heart'
      });
    }

    // Leaflet
    config.algorithmExtensions.set('leafletCommonAlgorithm', (builder: DataFlowBuilder): void => {
      buildLeafletCommonAlgorithm(
        builder,
        this._leaflet.map((l) => l.descriptor)
      );
    });

    // Commissure points
    config.algorithmExtensions.set('commissureCommonAlgorithm', (builder: DataFlowBuilder): void => {
      buildCommissureCommonAlgorithm(
        builder,
        this._commissure.map((cp) => cp.descriptor)
      );
    });

    for (const c of this._commissure) {
      config.points.push({
        importName: c.importName.toLowerCase(),
        algoName: 'pt_' + c.descriptor + '_imported',
        isOptionalEntity: false
      });
    }

    return config;
  }

  public getViewsConfig(): JSONConfig.Config {
    const config = configTTVR;

    const intraOpView = config.views.find((v) => v.descriptor == 'intra-op');
    if (!intraOpView) throw new Error('Logic Error');
    const deliveryView = config.views.find((v) => v.descriptor == 'delivery');
    if (!deliveryView) throw new Error('Logic Error');
    const deviceView = config.views.find((v) => v.descriptor == 'devicePlacement');
    if (!deviceView) throw new Error('Logic Error');
    const anatomyView = config.views.find((v) => v.descriptor == 'anatomy');
    if (!anatomyView) throw new Error('Logic Error');

    // Papillary distance measurements box
    const anatomyPapillaryViewMeasBox = anatomyView.measurementBoxes.find(
      (b) => b.groupDescriptor === TTVRPlanningEntityKind.Papillary
    )?.entries;
    if (!anatomyPapillaryViewMeasBox) throw new Error('Logic Error');

    // Leaflet distance measurements box
    const anatomyLeafletViewMeasBox = anatomyView.measurementBoxes.find(
      (b) => b.groupDescriptor === TTVRPlanningEntityKind.Leaflet
    )?.entries;
    if (!anatomyLeafletViewMeasBox) throw new Error('Logic Error');

    // Commissure point objects box
    const anatomyViewObjBox = anatomyView.objectBoxes.find(
      (b) => b.groupDescriptor === TTVRPlanningEntityKind.Commissure
    )?.entries;
    if (!anatomyViewObjBox) throw new Error('Logic Error');

    for (const p of this._papillary) {
      config.objects.push({
        descriptor: 'tp.pt_' + p.descriptor,
        label: p.name + ' Muscle Point',
        color: fuchsiaColor,
        selectionColor: LightenDarkenColor(fuchsiaColor, 10),
        highlightColor: LightenDarkenColor(fuchsiaColor, 10),
        attachments: ['var_' + p.descriptor + '_height'],
        kind: SceneItemKind.VariableDistancePoint
      });

      config.measurements.push({
        descriptor: 'var_' + p.descriptor + '_height',
        attachments: ['tp.pt_' + p.descriptor],
        label: p.name,
        syncSliceDescriptor: SliceType.AXIAL,
        color: color,
        imagesMode: ImagesMode.MPR,
        kind: SceneItemKind.LabeledDistanceMeasurement,
        details: {
          boxGroup: 'papillary'
        },
        ...angleMeasurementRelatedStub,
        ...centralLineStub
      });

      const papillaryPt = {
        descriptor: 'tp.pt_' + p.descriptor,
        transparency: Transparency.Opaque,
        isVisible: false,
        isContourVisible: false,
        isTunable: false
      };

      const papillaryMeasurement = {
        descriptor: 'var_' + p.descriptor + '_height',
        zoomFactor: 0.2,
        isVisible: false,
        isTunable: false
      };

      intraOpView.objects.push(papillaryPt);
      intraOpView.measurements.push(papillaryMeasurement);
      intraOpView.workflowIndications.push({ descriptor: 'var_' + p.descriptor + '_height' });

      deliveryView.objects.push(papillaryPt);
      deliveryView.measurements.push(papillaryMeasurement);
      deliveryView.workflowIndications.push({ descriptor: 'var_' + p.descriptor + '_height' });

      deviceView.objects.push(papillaryPt);
      deviceView.measurements.push(papillaryMeasurement);
      deviceView.workflowIndications.push({ descriptor: 'var_' + p.descriptor + '_height' });

      anatomyView.objects.push({
        ...papillaryPt,
        isPickable: true,
        isTunable: true
      });

      anatomyView.measurements.push(papillaryMeasurement);

      anatomyView.visibilityDependencies.push({
        item: 'tp.pt_' + p.descriptor,
        dependsOn: ['var_' + p.descriptor + '_height']
      });

      anatomyPapillaryViewMeasBox.push('var_' + p.descriptor + '_height');
      anatomyView.workflowIndications.push({ descriptor: 'var_' + p.descriptor + '_height' });
    }

    for (const c of this._commissure) {
      const _color = this._resolveCommissurePointColor(c.descriptor) ?? aquaColor;
      config.objects.push({
        descriptor: 'pt_' + c.descriptor,
        label: c.name,
        color: _color,
        selectionColor: _color,
        highlightColor: _color,
        attachments: ['device_roi', 'landing_zone_spline', 'tp.pt_' + c.descriptor + '_rotation'],
        syncSliceDescriptor: SliceType.SAGITTAL,
        imagesMode: ImagesMode.MPR,
        kind: SceneItemKind.CommissurePoint
      });

      const commissurePt = {
        descriptor: 'pt_' + c.descriptor,
        transparency: Transparency.Opaque,
        isVisible: true,
        isContourVisible: false,
        isPickable: false,
        isTunable: false,
        isClippable: false
      };

      intraOpView.objects.push(commissurePt);

      intraOpView.workflowIndications.push({ descriptor: 'pt_' + c.descriptor });

      deliveryView.objects.push(commissurePt);

      deliveryView.workflowIndications.push({ descriptor: 'pt_' + c.descriptor });

      deviceView.objects.push(commissurePt);
      deviceView.workflowIndications.push({ descriptor: 'pt_' + c.descriptor });

      anatomyView.objects.push({
        ...commissurePt,
        isPickable: true,
        isTunable: true
      });

      anatomyViewObjBox.push({
        descriptor: 'pt_' + c.descriptor,
        label: this._resolveCommissurePointLabel(c.descriptor) ?? c.name
      });
      anatomyView.workflowIndications.push({ descriptor: 'pt_' + c.descriptor });
    }

    return config;
  }

  private _resolveCommissurePointLabel(descriptor: string): string | undefined {
    if (descriptor == 'as') return 'Anteroseptal (AS)';
    if (descriptor == 'ap') return 'Anteroposterior (AP)';
    if (descriptor == 'ps') return 'Posteroseptal (PS)';
    return undefined;
  }

  private _resolveCommissurePointColor(descriptor: string): string | undefined {
    if (descriptor == 'as') return deepSkyBlueColorDarken;
    if (descriptor == 'ap') return neonGreenColor;
    if (descriptor == 'ps') return redColor;
    return undefined;
  }
}
